import LoggingAPI from "@/api/logging";
import { settleWithLimit } from "@/utils/settle-with-limit";

import { ContactsAPI } from "./contacts.api";
import { Contact } from "./contacts.types";

/**
 * How many contacts are linked at a time. A selection is not always a handful —
 * the picker's Ctrl/Cmd+A puts every loaded row in it — and each contact is up
 * to two requests, so a whole-set submit fanned out at once is hundreds of
 * requests queued behind the browser's per-host connection limit, with the
 * progress readout arriving as one jump at the end rather than counting up.
 *
 * Small enough to stay well inside that limit and leave room for whatever else
 * the page is doing, large enough that the round trips overlap.
 */
const LINK_CONCURRENCY = 5;

/**
 * What a run of `assignContactsToCase` did, split by what the server said about
 * each contact rather than by whether the request threw.
 *
 * `alreadyAssigned` is deliberately its own bucket rather than folded into
 * either of the others: it is not a failure — the user's intent is satisfied —
 * but nothing changed, so it is not work to report or to log either.
 */
export interface AssignOutcome {
  /** Newly linked: the server inserted a row. */
  linked: Contact[];
  /** Already on the case, so INSERT IGNORE dropped the row. */
  alreadyAssigned: Contact[];
  failed: { contact: Contact; reason: unknown }[];
}

/**
 * The contacts the case holds because of this run, whether it put them there or
 * found them already there.
 *
 * Both have to reach the caller. The case's contact list needs refetching
 * either way, and leaving an already-assigned contact ticked in the picker arms
 * a retry that can never accomplish anything.
 */
export const settledContacts = (outcome: AssignOutcome): Contact[] => [
  ...outcome.linked,
  ...outcome.alreadyAssigned,
];

interface LinkContactOptions {
  caseId: number;
  contact: Contact;
  userId?: number;
}

/**
 * One contact's link, followed by its log. Sequential on purpose: a failed
 * assignment must never leave a success log behind. Resolves to whether the
 * contact was already on the case.
 */
const linkContact = async ({
  caseId,
  contact,
  userId,
}: LinkContactOptions): Promise<{ alreadyAssigned: boolean }> => {
  /*
    skipErrorToast: the axios interceptor toasts every failed request before
    handleError resolves it as { success: false } (connection.js). This run
    reports its failures once, as a tally in the modal's snackbar; without the
    opt-out 20 failed links put 20 toasts on top of that, and notistack shows
    them a few at a time.
  */
  const res = await ContactsAPI.linkCaseContact(
    { case_id: caseId, client_id: contact.client_id },
    { skipErrorToast: true }
  );

  /*
    A bodyless 200 — what an API predating the `linked` flag answers a
    successful link with — reaches us as `""`. Normalising it to an empty body
    keeps the two checks below reading one shape: a response with nothing in it
    says nothing went wrong, it just says nothing (ENG-1688).
  */
  const body = typeof res === "object" && res !== null ? res : {};

  /*
    The API layer never rejects: an HTTP or network failure resolves as
    { success: false, error } (connection.js handleError). Turn that into a real
    rejection, or a 500 counts as a link, leaves a success log behind, and
    reports work that never happened.

    That shape is the only failure. Rejecting on a falsy response as well put
    every contact of a clean run into the failed bucket — reporting a failure
    against a case that had just linked every one of them.
  */
  if (body.success === false) {
    throw new Error(`Failed to link contact: ${contact.name}`);
  }

  /*
    linked === false means INSERT IGNORE dropped the row: the contact was
    already on the case and nothing changed, so there is no assignment to log.

    Only the server can make this call. A picker marks the contacts it knows
    about, but someone else can assign one while it sits open, and no amount of
    client-side marking sees that.

    An API predating the flag returns no `linked` at all; treat that as a link,
    which is what every response meant before it existed.
  */
  if (body.linked === false) return { alreadyAssigned: true };

  /*
    The link has already happened, so a failed log must not fail the contact
    with it: calling a link that exists a failure would keep it selected for a
    retry that links it a second time.

    It still has to be noticed, though, and the API layer resolves rather than
    rejects here too, so a catch alone never sees the log that did not get
    written. Only `success === false` marks that failure: unlike the link call
    above, a written log answers with an empty body, so treating any falsy
    response as a failure would report every successful log as a broken one.

    Swallowing it here does not reach the interceptor, which toasts before
    handleError resolves, so the log opts out of that toast too. Otherwise a
    run whose links all succeed shows a red toast and a success snackbar at
    once.
  */
  try {
    const logRes = await LoggingAPI.createLog(
      {
        user_id: userId,
        message: `Assigned Contact to case: ${contact.name}`,
        event: "contact:assign",
        object_type: "contact",
        object_uuid: contact?.uuid,
      },
      { skipErrorToast: true }
    );

    if (logRes?.success === false) {
      console.error(
        `Contact linked but assignment log failed: ${contact.name}`,
        logRes.error
      );
    }
  } catch (error) {
    console.error("Error logging contact assignment:", error);
  }

  return { alreadyAssigned: false };
};

interface AssignContactsToCaseOptions {
  caseId: number;
  contacts: Contact[];
  userId?: number;
  /**
   * Fires once per contact the run has finished with, failures included: this
   * is progress through the selection, not a success tally, so it is what lets
   * a readout reach the end of the run however the run went.
   */
  onProgress?: () => void;
}

/**
 * Links every contact to the case, LINK_CONCURRENCY at a time, and reports what
 * happened to each one.
 *
 * Never rejects for a contact: one failing must not hide the outcome of the
 * others, and a partial run is the case the caller most needs to describe.
 */
export const assignContactsToCase = async ({
  caseId,
  contacts,
  userId,
  onProgress,
}: AssignContactsToCaseOptions): Promise<AssignOutcome> => {
  const results = await settleWithLimit(
    contacts,
    LINK_CONCURRENCY,
    async (contact) => {
      try {
        return await linkContact({ caseId, contact, userId });
      } finally {
        onProgress?.();
      }
    }
  );

  const outcome: AssignOutcome = {
    linked: [],
    alreadyAssigned: [],
    failed: [],
  };

  /*
    One pass, pairing each contact with its own result. settleWithLimit returns
    in input order, but reading that order through three separate filters over
    two parallel arrays makes it a rule nothing enforces — pair them once here
    and every bucket below carries the contact it came from.
  */
  contacts.forEach((contact, index) => {
    const result = results[index];

    if (result.status === "rejected") {
      outcome.failed.push({ contact, reason: result.reason });
    } else if (result.value.alreadyAssigned) {
      outcome.alreadyAssigned.push(contact);
    } else {
      outcome.linked.push(contact);
    }
  });

  return outcome;
};

/** Snackbar copy for an outcome, and the variant that carries it. */
export interface AssignOutcomeMessage {
  message: string;
  variant: "success" | "warning" | "error";
}

/**
 * How a finished run reads to the user. Kept apart from the modal because the
 * interesting part is which of the outcome's three buckets is empty, not how it
 * is rendered — and there are more combinations of those than are comfortable
 * to check by eye.
 */
export const describeAssignOutcome = (
  outcome: AssignOutcome
): AssignOutcomeMessage => {
  const { linked, alreadyAssigned, failed } = outcome;

  if (failed.length === 0) {
    if (alreadyAssigned.length === 0)
      return { message: `Contacts Linked.`, variant: "success" };

    if (linked.length === 0)
      return {
        message: `Already on this case — nothing to link.`,
        variant: "success",
      };

    return {
      message: `Contacts Linked. ${alreadyAssigned.length} ${
        alreadyAssigned.length === 1 ? "was" : "were"
      } already on this case.`,
      variant: "success",
    };
  }

  // Nothing got through — including the no-ops, which are not a result the user
  // asked for but do mean the request reached the server.
  if (linked.length === 0 && alreadyAssigned.length === 0)
    return { message: `Error linking contacts to case.`, variant: "error" };

  const total = linked.length + alreadyAssigned.length + failed.length;

  return {
    message: `${failed.length} of ${total} contacts failed to link.`,
    variant: "warning",
  };
};
